import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listDispositionRules, upsertDispositionRule, deleteDispositionRule,
} from "@/lib/returns-rules.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Trash2, ArrowLeft } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/returns-rules")({ component: RulesPage });

const CONDS = ["any","new","used","damaged","destroy"] as const;
const ACTIONS = ["restock","refurb","scrap","return_to_vendor","quarantine"] as const;

function RulesPage() {
  const qc = useQueryClient();
  const { tenantId, customers, customerId } = useScope();
  const listFn = useServerFn(listDispositionRules);
  const upsertFn = useServerFn(upsertDispositionRule);
  const delFn = useServerFn(deleteDispositionRule);

  const { data } = useQuery({
    queryKey: ["disposition-rules", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const [scope, setScope] = useState<string>(customerId ?? "all");
  const [reason, setReason] = useState("");
  const [cond, setCond] = useState<typeof CONDS[number]>("any");
  const [action, setAction] = useState<typeof ACTIONS[number]>("restock");
  const [priority, setPriority] = useState(100);

  const add = useMutation({
    mutationFn: () => upsertFn({ data: {
      tenantId: tenantId!,
      customerId: scope === "all" ? null : scope,
      reasonCode: reason.trim() || null,
      condition: cond, suggestedAction: action, priority, active: true,
    } }),
    onSuccess: () => { toast.success("Rule added"); qc.invalidateQueries({ queryKey: ["disposition-rules"] }); setReason(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  const toggle = useMutation({
    mutationFn: (r: { id: string; active: boolean; tenantId: string; customer_id: string | null; reason_code: string | null; condition: any; suggested_action: any; priority: number }) =>
      upsertFn({ data: { id: r.id, tenantId: r.tenantId, customerId: r.customer_id, reasonCode: r.reason_code, condition: r.condition, suggestedAction: r.suggested_action, priority: r.priority, active: r.active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["disposition-rules"] }),
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { id } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["disposition-rules"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const cName = (id: string | null) => id ? (customers.find((c) => c.id === id)?.name ?? id.slice(0,8)) : "All customers";

  return (
    <>
      <PageHeader title="Disposition Rules" description="Auto-suggest a disposition action based on customer, reason and condition. Lower priority wins." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <div>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/returns"><ArrowLeft className="h-4 w-4 mr-1" />Back to returns</Link>
          </Button>
        </div>
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-6 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Customer scope</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All customers</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Reason code</Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="(any)" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Condition</Label>
              <Select value={cond} onValueChange={(v) => setCond(v as typeof CONDS[number])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONDS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Action</Label>
              <Select value={action} onValueChange={(v) => setAction(v as typeof ACTIONS[number])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Priority</Label>
              <Input type="number" value={priority} onChange={(e) => setPriority(Math.max(1, Number(e.target.value) || 100))} />
            </div>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !tenantId}>Add rule</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Priority</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Suggested action</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.rules ?? []).length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No rules.</TableCell></TableRow>}
                {(data?.rules ?? []).map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="tabular-nums">{r.priority}</TableCell>
                    <TableCell className="text-sm">{cName(r.customer_id)}</TableCell>
                    <TableCell className="text-sm">{r.reason_code ?? <span className="text-muted-foreground">any</span>}</TableCell>
                    <TableCell><Badge variant="secondary">{r.condition}</Badge></TableCell>
                    <TableCell><Badge>{r.suggested_action}</Badge></TableCell>
                    <TableCell>
                      <Switch checked={r.active} onCheckedChange={(v) => toggle.mutate({ id: r.id, active: v, tenantId: tenantId!, customer_id: r.customer_id, reason_code: r.reason_code, condition: r.condition, suggested_action: r.suggested_action, priority: r.priority })} />
                    </TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => del.mutate(r.id)} disabled={del.isPending}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}